// Dialog that forces the user to pick a patient from the list assigned to them
// before creating a new care program. Nurses cannot type names; admins also use
// it to keep data clean.

import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, User2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function PatientPickerDialog({ open, onOpenChange, onConfirm, isAdmin }) {
  const [patients, setPatients] = useState(null);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedId("");
    (async () => {
      try {
        const { data } = await api.get("/patients");
        setPatients(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Error cargando pacientes");
        setPatients([]);
      }
    })();
  }, [open]);

  const onPickAndConfirm = () => {
    const p = (patients || []).find((x) => x.id === selectedId);
    if (!p) {
      toast.error("Selecciona un paciente");
      return;
    }
    onConfirm(p);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-md overflow-y-auto" data-testid="patient-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <User2 className="h-5 w-5 text-[#1D4ED8]" /> Selecciona el paciente
          </DialogTitle>
          <DialogDescription>
            {isAdmin
              ? "Elige al paciente para el nuevo programa de cuidados."
              : "Solo puedes crear programas de los pacientes que el coordinador te haya asignado."}
          </DialogDescription>
        </DialogHeader>

        {patients === null ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : patients.length === 0 ? (
          <div
            className="rounded-xl border border-dashed border-amber-300 bg-amber-50 px-4 py-6 text-center text-sm font-semibold text-amber-900"
            data-testid="patient-picker-empty"
          >
            {isAdmin
              ? "Aun no hay pacientes registrados. Crea uno en 'Pacientes'."
              : "El coordinador aun no te ha asignado pacientes. Pidele que te asigne al menos uno."}
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="patient-picker-list">
            {patients.map((p) => {
              const active = selectedId === p.id;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(p.id)}
                    className={`flex w-full items-start justify-between gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
                      active
                        ? "border-[#1D4ED8] bg-blue-50 shadow-sm"
                        : "border-slate-200 bg-white hover:border-[#1D4ED8]"
                    }`}
                    data-testid={`patient-pick-${p.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900">{p.name}</div>
                      {(p.room || p.bed) && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="h-3 w-3" />
                          {p.room && p.room}
                          {p.room && p.bed && " #"}
                          {!p.room && p.bed && "#"}
                          {p.bed && p.bed}
                        </div>
                      )}
                      {p.notes && (
                        <div className="mt-1 line-clamp-1 text-[11px] italic text-slate-500">{p.notes}</div>
                      )}
                    </div>
                    <span
                      className={`mt-1 h-4 w-4 flex-shrink-0 rounded-full border-2 ${
                        active ? "border-[#1D4ED8] bg-[#1D4ED8]" : "border-slate-300"
                      }`}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
            data-testid="patient-picker-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={onPickAndConfirm}
            disabled={!selectedId}
            className="rounded-xl bg-[#1D4ED8] font-semibold hover:bg-[#1E40AF]"
            data-testid="patient-picker-confirm"
          >
            Crear programa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
